/**
 * Bilder für eingebettete Kommentar-Darstellung aufbereiten.
 *
 * Warum eine verkleinerte Kopie als data-URI und nicht ein Verweis auf unsere
 * Download-Route: ein solcher Verweis funktioniert nur dort, wo die passende
 * Anmeldung vorliegt. In Weclapps eigener Oberfläche zeigt er ins Leere (die
 * Adresse löst dort gegen weclapp.com auf), im Kundenportal fehlt die
 * Mitarbeiter-Anmeldung. Ein eingebettetes Bild trägt seine Daten bei sich und
 * wird deshalb überall angezeigt — in Weclapp, im Technikerportal, im
 * Kundenportal und in Mails.
 *
 * Die Originaldatei wird zusätzlich als Dokument ans Ticket gehängt. Genauso
 * verfahren Mailprogramme: kleine Vorschau im Text, vollständige Datei daneben.
 */

/** Längste Kante der eingebetteten Kopie. Darüber wird proportional verkleinert. */
const MAX_EDGE_PX = 1280;

/**
 * Obergrenze für die eingebettete Kopie. Ein Kommentar ist ein Textfeld, kein
 * Dateispeicher — wird es größer, verzichten wir auf das Einbetten und verweisen
 * nur noch auf den Anhang, statt den Kommentar unbrauchbar aufzublähen.
 */
const MAX_INLINE_BYTES = 400 * 1024;

export interface InlineImageResult {
  /** Fertige `data:`-URI der verkleinerten Kopie, oder null wenn zu groß/nicht möglich. */
  dataUri: string | null;
  /** Grund, wenn nicht eingebettet werden konnte — für eine ehrliche Rückmeldung. */
  reason?: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Bild konnte nicht gelesen werden'));
    };
    img.src = url;
  });
}

/**
 * Erzeugt eine verkleinerte, eingebettete Fassung des Bildes.
 *
 * Läuft bewusst im Browser: die Datei liegt dort ohnehin schon, und es erspart
 * eine zusätzliche Bibliothek im Backend — jede Abhängigkeitsänderung kostet dort
 * den Docker-Layer-Cache und damit Minuten pro Build.
 */
export async function buildInlineImage(file: File): Promise<InlineImageResult> {
  if (!file.type.startsWith('image/')) {
    return { dataUri: null, reason: 'Keine Bilddatei' };
  }

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { dataUri: null, reason: 'Browser kann das Bild nicht verkleinern' };

    // Weiß hinterlegen: PNG mit Transparenz würde als JPEG sonst schwarz werden.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const dataUri = canvas.toDataURL('image/jpeg', 0.72);
    // Grobe Größenabschätzung: base64 trägt 4 Zeichen je 3 Byte.
    const approxBytes = Math.ceil((dataUri.length - dataUri.indexOf(',') - 1) * 0.75);
    if (approxBytes > MAX_INLINE_BYTES) {
      return {
        dataUri: null,
        reason: 'Bild zu groß für die Vorschau im Text — hängt als Datei am Ticket.',
      };
    }
    return { dataUri };
  } catch (err) {
    return {
      dataUri: null,
      reason: err instanceof Error ? err.message : 'Bild konnte nicht aufbereitet werden',
    };
  }
}

/** Dateiname für die Anzeige im `alt`-Text, ohne Anführungszeichen. */
export function safeAltText(name: string): string {
  return name.replace(/["<>]/g, '').slice(0, 120);
}

/**
 * HTML des Editors in lesbaren Klartext wandeln.
 *
 * Weclapp speichert neben `htmlComment` immer auch ein `comment`-Feld. Systeme und
 * Ansichten, die nur das einfache Feld lesen, sollen dort etwas Sinnvolles finden —
 * deshalb Blockwechsel als Zeilenumbruch erhalten statt alles zusammenzuziehen.
 */
export function htmlToPlainText(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  el.querySelectorAll('br, p, div, li').forEach((n) => n.append('\n'));
  return (el.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Klartext zurück in Editor-HTML. Gegenstück zu `htmlToPlainText` für Texte, die
 * nicht aus dem Editor stammen (KIAN-Entwürfe): Leerzeile trennt Absätze, einfacher
 * Umbruch bleibt Umbruch. Der Text wird escaped — er kommt aus einem Modell und darf
 * kein Markup in den Editor schleusen.
 */
export function plainTextToHtml(text: string): string {
  const escape = (s: string): string => {
    const el = document.createElement('div');
    el.textContent = s;
    return el.innerHTML;
  };
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => `<p>${escape(block).replace(/\n/g, '<br />')}</p>`)
    .join('');
}
